// src/pages/admin/Logistics.jsx - COMPLETE WITH API KEY SUPPORT
import React, { useState, useEffect } from "react";
import { adminApi } from "../../api/adminApi";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Grid,
  Switch,
  Snackbar,
  InputAdornment,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Check,
  Close,
  Refresh,
  LocalShipping,
  MoreVert,
  AttachMoney,
  Speed,
  Straighten,
  VpnKey,
  Link as LinkIcon,
} from "@mui/icons-material";

const AdminLogistics = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [pricingList, setPricingList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Dialog State
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPricing, setEditingPricing] = useState(null);
  const [formData, setFormData] = useState({
    company: "",
    baseCost: "",
    distanceRate: "",
    weightRate: "",
    estimatedDelivery: 3,
    apiKey: "",
    apiUrl: "", // ✅ NEW
  });

  // Menu State
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPricing, setSelectedPricing] = useState(null);

  const fetchPricing = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminApi.getLogisticsPricing();
      setPricingList(response.data || []);
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to fetch logistics pricing",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPricing();
  }, []);

  const handleMenuOpen = (event, pricing) => {
    setAnchorEl(event.currentTarget);
    setSelectedPricing(pricing);
  };
  const handleMenuClose = () => setAnchorEl(null);

  const handleOpenDialog = (pricing = null) => {
    if (pricing) {
      setEditingPricing(pricing);
      setFormData({
        company: pricing.company || "",
        baseCost: pricing.baseCost?.toString() || "",
        distanceRate: pricing.distanceRate?.toString() || "",
        weightRate: pricing.weightRate?.toString() || "",
        estimatedDelivery: pricing.estimatedDelivery || 3,
        apiKey: pricing.apiKey || "",
        apiUrl: pricing.apiUrl || "",
      });
    } else {
      setEditingPricing(null);
      setFormData({
        company: "",
        baseCost: "",
        distanceRate: "",
        weightRate: "",
        estimatedDelivery: 3,
        apiKey: "",
        apiUrl: "",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPricing(null);
    setFormData({
      company: "",
      baseCost: "",
      distanceRate: "",
      weightRate: "",
      estimatedDelivery: 3,
      apiKey: "",
      apiUrl: "",
    });
    setError(null);
  };

  const handleSave = async () => {
    try {
      const data = {
        company: formData.company.trim(),
        baseCost: parseFloat(formData.baseCost) || 0,
        distanceRate: parseFloat(formData.distanceRate) || 0,
        weightRate: parseFloat(formData.weightRate) || 0,
        estimatedDelivery: parseInt(formData.estimatedDelivery) || 3,
        apiKey: formData.apiKey || null,
        apiUrl: formData.apiUrl || null,
      };

      if (editingPricing) {
        await adminApi.updateLogisticsPricing(editingPricing.id, data);
        setSuccess("Logistics pricing updated successfully!");
      } else {
        await adminApi.createLogisticsPricing(data);
        setSuccess("Logistics pricing created successfully!");
      }
      handleCloseDialog();
      fetchPricing();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to save logistics pricing",
      );
    }
  };

  const handleToggle = async (pricing) => {
    try {
      await adminApi.toggleLogisticsPricing(pricing.id);
      setSuccess(
        `"${pricing.company}" ${pricing.isActive ? "deactivated" : "activated"} successfully`,
      );
      fetchPricing();
      handleMenuClose();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to toggle status");
    }
  };

  const handleDelete = async (id) => {
    if (
      window.confirm("Are you sure you want to delete this logistics company?")
    ) {
      try {
        await adminApi.deleteLogisticsPricing(id);
        setSuccess("Logistics pricing deleted successfully!");
        fetchPricing();
        setTimeout(() => setSuccess(null), 3000);
        handleMenuClose();
      } catch (err) {
        setError(
          err.response?.data?.message || "Failed to delete logistics pricing",
        );
      }
    }
  };

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );

  return (
    <Box sx={{ pb: { xs: 8, sm: 10 } }}>
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSuccess(null)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {success}
        </Alert>
      </Snackbar>

      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 3 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Box>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold">
              Logistics Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure shipping companies, rates, API keys, and delivery
              estimates
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} width={{ xs: "100%", sm: "auto" }}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchPricing}
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              size={isMobile ? "small" : "medium"}
              fullWidth={isMobile}
              sx={{ borderRadius: 2 }}
            >
              Add Company
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isMobile ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {pricingList.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: "center", borderRadius: 3 }}>
              <Typography color="text.secondary">
                No logistics companies found
              </Typography>
            </Paper>
          ) : (
            pricingList.map((pricing) => (
              <Card key={pricing.id} sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Stack spacing={1.5}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <LocalShipping color="primary" />
                        <Typography variant="subtitle1" fontWeight="bold">
                          {pricing.company}
                        </Typography>
                      </Stack>
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, pricing)}
                      >
                        <MoreVert />
                      </IconButton>
                    </Stack>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip
                        icon={<AttachMoney />}
                        label={`Base: ${pricing.baseCost}`}
                        size="small"
                      />
                      <Chip
                        icon={<Speed />}
                        label={`${pricing.distanceRate}/km`}
                        size="small"
                      />
                      <Chip
                        icon={<Straighten />}
                        label={`${pricing.weightRate}/kg`}
                        size="small"
                      />
                      <Chip
                        label={`${pricing.estimatedDelivery} days`}
                        size="small"
                        color="info"
                      />
                    </Stack>
                    {pricing.apiKey && (
                      <Chip
                        icon={<VpnKey />}
                        label="API Connected"
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    )}
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Chip
                        icon={pricing.isActive ? <Check /> : <Close />}
                        label={pricing.isActive ? "Active" : "Inactive"}
                        size="small"
                        color={pricing.isActive ? "success" : "error"}
                      />
                      <Switch
                        checked={pricing.isActive}
                        onChange={() => handleToggle(pricing)}
                        size="small"
                      />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))
          )}
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: "grey.50" }}>
                <TableRow>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Company
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Base Cost
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Distance Rate
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Weight Rate
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Delivery
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      API
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Status
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle2" fontWeight="bold">
                      Actions
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pricingList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: "center", py: 4 }}>
                      <Typography color="text.secondary">
                        No logistics companies found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  pricingList.map((pricing) => (
                    <TableRow key={pricing.id} hover>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <LocalShipping color="primary" />
                          <Typography variant="body2" fontWeight="600">
                            {pricing.company}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>${pricing.baseCost}</TableCell>
                      <TableCell>${pricing.distanceRate}/km</TableCell>
                      <TableCell>${pricing.weightRate}/kg</TableCell>
                      <TableCell>{pricing.estimatedDelivery} days</TableCell>
                      <TableCell>
                        {pricing.apiKey ? (
                          <Chip
                            label="Connected"
                            size="small"
                            color="success"
                          />
                        ) : (
                          <Chip label="Not Set" size="small" color="default" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={pricing.isActive ? <Check /> : <Close />}
                          label={pricing.isActive ? "Active" : "Inactive"}
                          size="small"
                          color={pricing.isActive ? "success" : "error"}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, pricing)}
                        >
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            handleOpenDialog(selectedPricing);
            handleMenuClose();
          }}
        >
          <Edit fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={() => handleToggle(selectedPricing)}>
          {selectedPricing?.isActive ? (
            <>
              <Close fontSize="small" sx={{ mr: 1 }} /> Deactivate
            </>
          ) : (
            <>
              <Check fontSize="small" sx={{ mr: 1 }} /> Activate
            </>
          )}
        </MenuItem>
        <MenuItem
          onClick={() => handleDelete(selectedPricing?.id)}
          sx={{ color: "error.main" }}
        >
          <Delete fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, m: { xs: 1, sm: 2 } } }}
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">
            {editingPricing
              ? "Edit Logistics Company"
              : "Add New Logistics Company"}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Company Name"
              value={formData.company}
              onChange={(e) =>
                setFormData({ ...formData, company: e.target.value })
              }
              required
              placeholder="e.g., Ethio Post, DHL, FedEx"
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Base Cost (ETB)"
                  type="number"
                  value={formData.baseCost}
                  onChange={(e) =>
                    setFormData({ ...formData, baseCost: e.target.value })
                  }
                  InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Delivery (Days)"
                  type="number"
                  value={formData.estimatedDelivery}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      estimatedDelivery: e.target.value,
                    })
                  }
                  InputProps={{ inputProps: { min: 1 } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Distance Rate (ETB/km)"
                  type="number"
                  value={formData.distanceRate}
                  onChange={(e) =>
                    setFormData({ ...formData, distanceRate: e.target.value })
                  }
                  InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Weight Rate (ETB/kg)"
                  type="number"
                  value={formData.weightRate}
                  onChange={(e) =>
                    setFormData({ ...formData, weightRate: e.target.value })
                  }
                  InputProps={{ inputProps: { min: 0, step: 0.01 } }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="API Key (Optional)"
                  value={formData.apiKey}
                  onChange={(e) =>
                    setFormData({ ...formData, apiKey: e.target.value })
                  }
                  placeholder="Leave empty to auto-generate tracking numbers"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <VpnKey />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="API URL (Optional)"
                  value={formData.apiUrl}
                  onChange={(e) =>
                    setFormData({ ...formData, apiUrl: e.target.value })
                  }
                  placeholder="https://api.example.com"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LinkIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1, sm: 0 },
          }}
        >
          <Button onClick={handleCloseDialog} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!formData.company || formData.company.trim() === ""}
            fullWidth={isMobile}
            sx={{ borderRadius: 2 }}
          >
            {editingPricing ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminLogistics;
